/**
  * @prop --width: The width of the card. Defaults to 'unset'.
  * @prop --min-width: The minimum width of the card. Defaults to 128px.
  * @prop --image-margin: The negative margin applied to the image slot. It is calculated as the negative value of the '--padding' variable.
  * @prop --background-color: The background color of the card. Defaults to the value of '--s-color-background-default-default'.
  * @prop --text-color: The color of the text content within the card. Defaults to the value of '--s-color-text-default'.
  * @prop --border-color: The color of the card's border. Defaults to the value of '--s-color-border-default'.
  * @prop --border-width: The width of the card's border. Defaults to var(--p-size-static-0125).
  * @prop --border-radius: The border radius of the card. Defaults to var(--s-size-radius-medium).
  * @prop --focus-color: The color of the card's border when it is in focus. Defaults to the value of '--color-border-outline'.
  * @prop --focus-offset: The offset applied to the card's focus border. Defaults to var(--p-size-static-025).
  * @prop --padding: The padding applied to the card's content area. Defaults to var(--p-size-static-300).
  * @prop --icon-color: The color of the icon within the card. Defaults to the value of '--color-icon-variant'.
  * @prop --shadow: The shadow effect applied to the card. Defaults to 'none'.
*/

:host {
  display: block;
  --width: unset;
  --min-width: 128px;
  --image-margin: calc(var(--padding) * -1);
  --background-color: var(--s-color-background-default-default);

  // Text
  --text-color: var(--s-color-text-default);

  // Border
  --border-color: var(--s-color-border-default);
  --border-width: var(--p-size-static-0125);
  --border-radius: var(--s-size-radius-medium);

  // Focus
  --focus-color: var(--s-color-outline-default);
  --focus-offset: var(--s-size-outline-focus-ring-offset);

  // Padding
  --padding: var(--p-size-static-300);

  // Icon
  --icon-color: var(--s-color-icon-variant);

  // Shadow
  --shadow: none;

  width: var(--width);
  min-width: var(--min-width);

  // Slots
  ::slotted([slot|='image']) {
    margin: var(--image-margin) var(--image-margin) var(--p-size-static-200);
    grid-area: Image;
  }
  ::slotted([slot|='illustration']) {
    grid-area: Image;
    margin-bottom: var(--p-size-static-100);
    --product-illustration-color: var(--icon-color);
  }
  ::slotted([slot|='header']) {
    grid-area: Header;
    color: var(--s-color-text-default);
    font-weight: var(--s-font-weight-heading-emphasized);
    font-size: var(--s-size-text-heading-small-default);
    line-height: var(--s-size-line-height-heading-large-default);
    text-decoration: none;
    margin: 0;
  }
  ::slotted([slot|='text']) {
    grid-area: Text;
    color: var(--s-color-text-default);
    font-size: var(--s-size-text-default);
    line-height: var(--s-size-line-height-paragraph);
    margin: 0;
  }
  // Parts
  &::part(icon) {
    grid-area: Icon;
    --icon-color: var(--s-color-icon-variant);
  }
}

.anchor-c-card {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: max-content max-content max-content;
  gap: 0px 0px;
  grid-auto-flow: row;
  grid-template-areas:
    'Image'
    'Header'
    'Text';
  cursor: pointer;
  box-shadow: var(--shadow);
  overflow: hidden;
  box-sizing: border-box;
  padding: var(--padding);
  border-radius: var(--border-radius);
  background-color: var(--background-color);
  text-decoration: none;
  height: 100%; // This is necessary for the card to fill its parent container (the custom html element)
  &.icon {
    grid-template-columns: 1fr var(--p-size-static-400);
    grid-template-areas:
      'Image Image'
      'Header Icon'
      'Text Text';
  }
  &.border {
    --padding: calc(var(--p-size-static-300) - var(--border-width));
    border: var(--border-width) solid var(--border-color);
  }
  &.center {
    text-align: center;
    ::slotted([slot|='illustration']) {
      display: flex;
      justify-content: center;
    }
  }
  &.left {
    text-align: left;
  }
  &.hover,
  &:hover {
    --border-width: var(--p-size-static-025);
    border: var(--border-width) solid var(--border-color);
    --padding: calc(var(--p-size-static-300) - var(--border-width));
  }
  &.focus,
  &:focus {
    outline: var(--s-size-outline-focus-ring-width) solid var(--focus-color);
    outline-offset: var(--focus-offset);
  }
  &.pressed,
  &:active {
    --background-color: var(--s-color-background-default-pressed);
    outline: none;
  }

  &__vertical {
    &-top {
      align-content: flex-start;
    }

    &-center {
      align-content: center;
    }

    &-bottom {
      align-content: flex-end;
    }
  }
}
